import { useState, useEffect } from 'react';
import { FileImage, CheckCircle, XCircle, Clock } from 'lucide-react';
import api from '@/services/api';
import { Prescription } from '@/types';
import { formatDateTime } from '@/utils';
import { Button } from '@/components/ui/button';
import toast from '@/hooks/useToast';

interface PrescriptionWithUser extends Prescription {
  user: { name: string; email: string; phone?: string };
  patientNotes?: string;
}

export const PharmacistDashboard = () => {
  const [prescriptions, setPrescriptions] = useState<PrescriptionWithUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionNotes, setActionNotes] = useState<Record<string, string>>({});
  const [processing, setProcessing] = useState<string | null>(null);

  const fetchPending = () => {
    setLoading(true);
    api.get('/prescriptions/pending')
      .then(res => setPrescriptions(res.data.data))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchPending(); }, []);

  const handleVerify = async (id: string) => {
    setProcessing(id);
    try {
      await api.put(`/prescriptions/${id}/verify`, { notes: actionNotes[id] });
      setPrescriptions(prev => prev.filter(p => p.id !== id));
      toast.success('Prescription verified!');
    } catch { toast.error('Could not verify prescription'); }
    finally { setProcessing(null); }
  };

  const handleReject = async (id: string) => {
    setProcessing(id);
    try {
      await api.put(`/prescriptions/${id}/reject`, { notes: actionNotes[id] || 'Prescription rejected by pharmacist.' });
      setPrescriptions(prev => prev.filter(p => p.id !== id));
      toast.success('Prescription rejected');
    } catch { toast.error('Could not reject prescription'); }
    finally { setProcessing(null); }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <div className="flex items-center gap-3 mb-8">
        <div className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center">
          <FileImage className="h-5 w-5 text-primary-600" />
        </div>
        <div>
          <h1 className="font-heading font-bold text-2xl text-gray-800">Pharmacist Review Queue</h1>
          <p className="text-gray-500 text-sm">{prescriptions.length} prescription(s) pending review</p>
        </div>
      </div>

      {loading ? (
        <div className="space-y-4">{Array(3).fill(0).map((_, i) => <div key={i} className="h-48 bg-gray-100 rounded-2xl animate-pulse" />)}</div>
      ) : prescriptions.length === 0 ? (
        <div className="text-center py-16">
          <CheckCircle className="h-16 w-16 text-success mx-auto mb-4" />
          <h3 className="font-heading font-semibold text-xl text-gray-700 mb-2">All clear!</h3>
          <p className="text-gray-400">No prescriptions pending review.</p>
        </div>
      ) : (
        <div className="space-y-6">
          {prescriptions.map(p => (
            <div key={p.id} className="bg-white rounded-card shadow-card overflow-hidden">
              <div className="flex">
                {/* Image */}
                <div className="w-48 shrink-0 bg-gray-50">
                  <a href={p.imageUrl} target="_blank" rel="noopener noreferrer">
                    <img src={p.imageUrl} alt="Prescription" className="w-full h-full object-cover" />
                  </a>
                </div>
                {/* Details */}
                <div className="flex-1 p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <p className="font-semibold text-gray-800">{p.user.name}</p>
                      <p className="text-sm text-gray-400">{p.user.email}</p>
                      {p.user.phone && <p className="text-sm text-gray-400">{p.user.phone}</p>}
                    </div>
                    <div className="flex items-center gap-1.5 bg-yellow-50 text-yellow-700 text-xs font-semibold px-3 py-1 rounded-full">
                      <Clock className="h-3.5 w-3.5" /> Pending
                    </div>
                  </div>
                  <p className="text-xs text-gray-400 mb-3">Submitted: {formatDateTime(p.createdAt)}</p>

                  {p.patientNotes && (
                    <div className="mb-3 bg-blue-50 rounded-xl px-3 py-2 text-sm text-blue-800">
                      <span className="font-semibold">Patient note: </span>{p.patientNotes}
                    </div>
                  )}

                  <div className="mb-3">
                    <label className="block text-xs font-medium text-gray-500 mb-1">Notes (optional)</label>
                    <textarea
                      value={actionNotes[p.id] || ''}
                      onChange={e => setActionNotes(prev => ({ ...prev, [p.id]: e.target.value }))}
                      placeholder="Add pharmacist notes for the patient..."
                      className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 resize-none h-16"
                    />
                  </div>

                  <div className="flex gap-3">
                    <Button
                      size="sm"
                      loading={processing === p.id}
                      onClick={() => handleVerify(p.id)}
                      className="bg-success hover:bg-green-600"
                    >
                      <CheckCircle className="h-4 w-4 mr-1.5" /> Verify
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      loading={processing === p.id}
                      onClick={() => handleReject(p.id)}
                    >
                      <XCircle className="h-4 w-4 mr-1.5" /> Reject
                    </Button>
                    <a href={p.imageUrl} target="_blank" rel="noopener noreferrer" className="text-sm text-primary-600 hover:text-primary-700 font-medium self-center ml-auto">
                      View Full Image →
                    </a>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
